import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { randomId } from '@mui/x-data-grid-generator'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import RowActionsCell from 'components/generic/datagrid/cells/RowActionsCell'
import BasicDataGrid from 'components/generic/datagrid'
import CheckboxCell from 'components/generic/datagrid/cells/CheckboxCell'
import DialogModal from '.'

export const formatNotes = notes =>
  notes?.map(note => ({
    ...note,
    isInternal: note.isInternal,
  })) || []

const NotesModal = ({ onSave, notes, disabled, ...restProps }) => {
  const apiRef = useGridApiRef()

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      editable: !disabled,
      sortable: false,
    },
    {
      field: 'isInternal',
      headerName: 'Internal',
      renderCell: params => <CheckboxCell {...params} disabled={disabled} />,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      renderCell: params => <RowActionsCell {...params} disabled={disabled} />,
    },
  ]

  const getNewNote = () => ({
    id: randomId(),
    name: '',
    isInternal: false,
  })

  const transformedRows = notes.length ? notes : [getNewNote()]

  const onClickNewNote = useCallback(() => {
    const id = randomId()
    apiRef.current.updateRows([{ id, isInternal: false }])
    apiRef.current.setRowMode(id, 'edit')
    // Wait for the grid to render with the new row
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      })

      apiRef.current.setCellFocus(id, 'name')
    }, 150)
  }, [apiRef])

  return (
    <DialogModal
      {...restProps}
      hideDialogActions={disabled}
      onSave={() => {
        const rowIds = apiRef.current.getAllRowIds()

        rowIds.forEach(id => {
          const rowMode = apiRef.current.getRowMode(id)
          if (rowMode === 'edit') apiRef.current.commitRowChange(id)
        })

        onSave([...apiRef.current.getRowModels().values()])
      }}
    >
      <Box p={1} sx={{ height: 450, overflow: 'scroll' }}>
        <BasicDataGrid
          density="compact"
          height="auto"
          rows={transformedRows}
          columns={columns}
          apiRef={apiRef}
          editMode="row"
          disableColumnMenu
          hideFooter
        />
        {!disabled && (
          <Box mt={1}>
            <Button startIcon={<AddIcon />} onClick={onClickNewNote}>
              Add Another Note
            </Button>
          </Box>
        )}
      </Box>
    </DialogModal>
  )
}

NotesModal.propTypes = {
  ...DialogModal.propTypes,
  notes: PropTypes.array,
  disabled: PropTypes.bool,
}

NotesModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Notes',
  notes: [],
  disabled: false,
}

export default NotesModal
